import {t} from '@/i18n'
import Idea from '@/types/Idea'
import {EnhancedWorkpace} from '@/types/Workspace'
import {RouteLocationNormalizedLoaded, RouteLocationRaw} from 'vue-router'

/**
 * O endereço da tela atual, montado a partir da rota e do workspace.
 *
 * Mora fora do App.vue pelo mesmo motivo que os cabeçalhos moram fora das telas
 * (ver utils/heading.ts): é uma regra por nome de rota, e dentro do componente ela
 * viraria uma dúzia de `v-if` no template.
 *
 * Cada degrau tem um RÓTULO — o que a coisa é — e um TEXTO — qual coisa é. "Coleção /
 * Canções", e não só "Canções": o rótulo é o que deixa o caminho legível para quem
 * ainda não decorou o vocabulário do ambiente.
 *
 * **Menos nos degraus que são um VERBO.** "Criar" e "Editar" não são uma coisa de um tipo: são
 * o que se está fazendo com o degrau de cima. Rotulá-los pediria uma palavra para a categoria
 * "ação", que não existe no vocabulário do ambiente — e "Item / Criar" diria que se está vendo
 * um item chamado Criar. Esses vêm sem rótulo, com a palavra sozinha.
 *
 * `t()` dentro da função, nunca no corpo do módulo: traduzir no carregamento
 * congelaria o idioma no que estivesse ativo naquele instante.
 */

export type Crumb = {
    /** Ausente nos degraus de ação — ver o cabeçalho deste arquivo. */
    label?: string
    text: string
    to: RouteLocationRaw
}

/**
 * O destino de uma coleção, que é UM SÓ: a lista ou o Kanban dela, de qualquer tela — também das do
 * design (pedido do usuário). Já houve um segundo, o desenho dela, quando a casca tinha um modo de
 * design; ele saiu com os modos, e o design é um degrau abaixo da coleção.
 *
 * Vive aqui, e não na aba, porque a aba da barra, o degrau do breadcrumb e o botão da coleção na faixa
 * precisam apontar para o mesmo lugar — se divergirem, cada um leva a uma tela diferente para a
 * mesma coleção.
 *
 * A rota da coleção decide entre lista e Kanban pela última visão usada e troca a URL por ela. A
 * regra mora lá, e não aqui, para valer também para quem digita o endereço ou volta por um link
 * salvo — ver CollectionEntry.vue.
 */
export function collectionRoute(idea: Idea, system: string): RouteLocationRaw {
    return {name: 'collection', params: {system, idea: idea.alias}}
}

/** O design de uma coleção: o menu "Configurar" da barra e o degrau DESIGN do caminho. */
export function collectionDesignRoute(idea: Idea, system: string): RouteLocationRaw {
    return {name: 'view-idea', params: {system, idea: idea.alias}}
}

/**
 * A aba de uma tela em abas, na URL (`?tab=`) — ver o `TabbedSection`, que a lê e a escreve. A aba
 * com que a tela ABRE não vai: sem pedido, é ela.
 */
export function tabQuery(tab: string, opens: string): Record<string, string> {
    return tab === opens ? {} : {tab}
}

/**
 * As abas do design do ambiente, na ordem da tela. A primeira é Informações, mas a tela ABRE em
 * Coleções, que é o que mais se usa ali (escolha do usuário). A última, Membros, só existe para o
 * administrador — quem a tira para os outros é a tela (`SystemDesign`).
 */
export const DESIGN_TABS = ['info', 'collections', 'workflows', 'domains', 'roles', 'members'] as const
export type DesignTab = typeof DESIGN_TABS[number]
export const DESIGN_OPENS: DesignTab = 'collections'

/**
 * O design do ambiente, aberto numa aba. Na URL, e não em estado da tela, para o F5 e o voltar do
 * navegador manterem a aba, e para quem sai de uma parte — a ficha de um domínio excluído, a edição
 * do ambiente — voltar à aba de onde veio.
 */
export function workspaceDesignRoute(system: string, tab: DesignTab = DESIGN_OPENS): RouteLocationRaw {
    return {name: 'design-system', params: {system}, query: tabQuery(tab, DESIGN_OPENS)}
}

/**
 * As abas do design de uma coleção, no mesmo arranjo do ambiente: Informações primeiro, e a tela
 * abrindo no que mais se usa ali, as propriedades.
 */
export const COLLECTION_DESIGN_TABS = ['info', 'properties', 'workflow', 'access'] as const
export type CollectionDesignTab = typeof COLLECTION_DESIGN_TABS[number]
export const COLLECTION_DESIGN_OPENS: CollectionDesignTab = 'properties'

/**
 * O degrau de uma propriedade: "Propriedade / Tarefas".
 *
 * Os dois lugares que o usam — o design de uma propriedade e o Kanban de um relacionamento —
 * montavam o mesmo `find` e o mesmo degrau. O destino é a PRÓPRIA rota: é o último degrau, e
 * continua clicável de propósito, recarregando o nível em que se está.
 */
function propertyCrumb(idea: Idea, route: RouteLocationNormalizedLoaded): Crumb | null {
    const property = (idea.properties || []).find(p => p.alias === route.params.property)
    if (!property) return null
    return {
        label: t('property'),
        text: property.name,
        to: {name: route.name as string, params: route.params}
    }
}

/**
 * O degrau do que se está FAZENDO: "Criar", "Editar".
 *
 * Sem rótulo, e apontando para a própria rota — é o último degrau, e como os outros continua
 * clicável, recarregando o nível em que se está.
 *
 * As palavras são as que os botões já usam — `new` é "Criar" e `edit` é "Editar" —, para o
 * caminho dizer a mesma coisa que o botão que levou até ele.
 */
function actionCrumb(key: Parameters<typeof t>[0], route: RouteLocationNormalizedLoaded): Crumb {
    return {text: t(key) as string, to: {name: route.name as string, params: route.params}}
}

export function breadcrumb(
    route: RouteLocationNormalizedLoaded,
    workspace: EnhancedWorkpace | null,
    instanceLabel: string | null
): Crumb[] {
    const system = route.params.system as string
    //workspace nasce como {} no App e só vira objeto de verdade depois do carregamento;
    //sem o ideaWith não há o que montar, e a home não tem ambiente nenhum.
    if (!system || !workspace || !workspace.ideaWith) return []

    //O primeiro degrau é o início do ambiente, de qualquer tela. Era a home do MODO em que se
    //estava — o diagrama, no design —, e os modos saíram (ver `utils/destinations`)
    const crumbs: Crumb[] = [{
        label: t('workspace'),
        text: workspace.title,
        to: {name: 'view-system', params: {system}}
    }]

    //O design é um degrau de ação, como CRIAR e EDITAR: "Ambiente › DESIGN", "Coleção › DESIGN"
    //(pedido do usuário). Só nas duas telas de design; as partes dele — fluxo, domínio, propriedade —
    //continuam direto sob o ambiente ou a coleção (decisão do usuário).
    if (route.name === 'design-system') return [...crumbs, actionCrumb('design-verb', route)]

    const idea = route.params.idea ? workspace.ideaWith(route.params.idea as string) : undefined
    if (idea) {
        crumbs.push({
            label: t('collection'),
            //Sempre no plural e sempre para a lista ou o Kanban, também de dentro do design dela
            //(pedido do usuário): o degrau é a coleção, e o design é o degrau seguinte.
            text: idea.pluralName,
            to: collectionRoute(idea, system)
        })
        if (route.name === 'view-idea') return [...crumbs, actionCrumb('design-verb', route)]
    } else if (route.params.workflow) {
        const workflow = workspace.workflowWith(route.params.workflow as string)
        if (workflow) crumbs.push({
            label: t('workflow'),
            text: workflow.name,
            to: {name: 'view-workflow', params: {system, workflow: workflow.alias}}
        })
    } else if (route.params.domain) {
        const domain = workspace.domainWith(route.params.domain as string)
        if (domain) crumbs.push({
            label: t('domain'),
            text: domain.name,
            to: {name: 'view-domain', params: {system, domain: domain.alias}}
        })
    }

    if (!idea) return crumbs

    //Só COM o rótulo que a tela publica. Enquanto o item não carrega, o degrau
    //simplesmente não existe: o id interno ("Canção #42") é o número da linha no banco,
    //não diz nada a quem lê, e mostrá-lo seria encher o caminho com um endereço falso.
    //Prefere-se o caminho crescer um degrau ao carregar a mostrar o degrau errado.
    //
    //Os dois ramos são exclusivos de propósito: a rota do Kanban de relacionamento tem
    //instância E propriedade, e numa condição só ela cairia no ramo da propriedade e
    //mostraria o nome do campo como se fosse o item. Estando separados, a rota de instância
    //nunca é tratada como outra coisa — e, dentro dela, a propriedade vira o degrau seguinte.
    if (route.params.instance) {
        //Sem o rótulo do item não há degrau nenhum daqui para baixo. O do Kanban de um
        //relacionamento é um degrau ABAIXO do item — a mesma instância, vista por um campo
        //dela —, e sozinho ficaria pendurado no nome da coleção, dizendo "Tarefas" sem dizer
        //de quem. Prefere-se o caminho crescer um degrau ao carregar.
        if (!instanceLabel) return crumbs
        crumbs.push({
            //O rótulo é o nome da coleção no singular: "Canção / Conversa de botequim".
            label: idea.name,
            text: instanceLabel,
            to: {
                name: 'view-instance',
                params: {system, idea: idea.alias, instance: route.params.instance}
            }
        })
        //A propriedade é da coleção DONA, que é a que a rota endereça — e o `idea` aqui é ela.
        const property = route.params.property ? propertyCrumb(idea, route) : null
        if (property) crumbs.push(property)
        else if (route.name === 'edit-instance') crumbs.push(actionCrumb('edit', route))
    } else if (route.params.property) {
        const property = propertyCrumb(idea, route)
        if (property) crumbs.push(property)
    } else if (route.name === 'create-instance') {
        crumbs.push(actionCrumb('new', route))
    }

    return crumbs
}
